<template>
    <div class="bg-white">
        <div class="bg-gray-50 border-gray-200 border-b border-t">
            <div class="p-5 mx-auto md:w-2/3 lg:w-1/2 xl:w-1/3 2xl:w-1/4">
                <div v-if="transactions && transactions.length > 1" class="flex-shrink-0 group block">
                    <div class="flex items-center">
                        <div :class="`text-${$root.theme}-800`">
                            <svg class="inline-block h-10 w-10" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clip-rule="evenodd" />
                            </svg>
                        </div>
                        <div class="ml-3">
                            <p :class="`pt-1 font-semibold text-lg text-${$root.theme}-500 group-hover:text-${$root.theme}-600`">
                                Payment plan has been confirmed!
                            </p>
                        </div>
                    </div>
                </div>
                <div v-else class="flex-shrink-0 group block mb-2">
                    <div class="flex items-center">
                        <div>
                            <svg class="inline-block h-10 w-10" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clip-rule="evenodd" />
                            </svg>
                        </div>
                        <div class="ml-3">
                            <p class="pt-1 font-semibold text-lg">
                                Thank you for your payment!
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <electronic-consent
            v-if="shouldAskForElectronicConsent"
            :template="electronicConsentTemplate"
            :declined-template="electronicConsentDeclinedTemplate"
            :consumer-portal-data="consumerPortalData"
            :account-id="details.account_id"
            source="offer-summary"
            @accepted="acceptEcomm"
            @declined="declineEcomm"
        />
        <gather-contact-information
            v-else-if="shouldCollectContactInformation"
            :account="account"
            @done="shouldCollectContactInformation = false"
        />
        <feedback-component v-else-if="shouldAskForFeedback"
            :account-id="details.account_id"
            :email="account.email"
            :consumer-portal-data="consumerPortalData"
            :redirect="`${this.consumerPortalData.consumerPortalBaseUrl}/account`"
            :show-link="true"
            referral="offer-summary"
        />
        <a v-else :href="`${this.consumerPortalData.consumerPortalBaseUrl}/account`" class="w-full justify-center text-black underline inline-flex text-xs leading-4 font-medium mt-4">
            Take me to Account Summary
        </a>

        <div v-if="! shouldAskForElectronicConsent" class="p-5 mx-auto md:w-2/3 lg:w-1/2 xl:w-1/3 2xl:w-1/4">
            <div class="group block">
                <p class="font-medium text-sm">
                    If you have any questions, please feel free to reach us at <a :href="`tel:${$root.phoneNumberForLocation('footer')}`" style="white-space: nowrap;">{{ $root.phoneNumberForLocation('footer') }}</a>.
                </p>
            </div>
        </div>
    </div>
</template>

<script>
    import Moment from 'moment';
    import FeedbackComponent from './FeedbackComponent';
    import ElectronicConsent from "../components/ElectronicConsent.vue";
    import GatherContactInformation from "./GatherContactInformation.vue";

    export default {
        props: [
            'error',
            'details',
            'account',
            'settings',
            'transactions',
            'payment-date',
            'selected-offer',
            'consumer-portal-data',
            'electronic-consent-template',
            'electronic-consent-declined-template',
        ],

        components: {
            GatherContactInformation,
            ElectronicConsent,
            FeedbackComponent
        },

        mounted() {
            if (this.$root.loginReferral === 'Email' && this.account.has_gdn_numbers) {
                this.shouldCollectContactInformation = false;
            }
        },

        data() {
            return {
                showLink: false,
                shouldCollectContactInformation: true,
                shouldAskForFeedback: window.ask_for_feedback,
                shouldAskForElectronicConsent: !! this.electronicConsentTemplate,
                electronicConsentCheckbox: false,
                elfSight: window.elf_sight,
                elfSightId: window.elf_sight_id,
                rating: null,
                submitted: false,
                feedback: null,
                hover: {
                    1: false,
                    2: false,
                    3: false,
                    4: false,
                    5: false
                }
            }
        },

        computed: {
            /**
             * @returns {null|Object}
             */
            firstPayment() {
                if (! this.transactions || ! this.transactions.length) return null;

                return this.transactions[0];
            },

            /**
             * @returns {null|Object}
             */
            nextPayment() {
                if (! this.transactions || ! this.transactions.length || this.transactions.length < 2) return null;

                return this.transactions[1];
            },

            /**
             * @returns {Boolean}
             */
            isFuture() {
                return Moment(this.paymentDate).isAfter(Moment().endOf(('day')));
            },
        },

        methods: {
            acceptEcomm() {
                this.shouldAskForElectronicConsent = false;
            },

            declineEcomm() {
                this.shouldCollectContactInformation = false;
                this.shouldAskForElectronicConsent = false;
            },

            starHover(nRating) {
                for (let i = 1; i <= 5; i++) {
                    this.hover[i] = i <= nRating;
                }
            },

            starLeave() {
                for (let i = 1; i <= 5; i++) {
                    this.hover[i] = false;
                }
            },

            setRating(numberOfStars) {
                if (! this.submitted) {
                    this.rating = numberOfStars;
                }
            }
        }
    }
</script>
